import { Fragment } from 'react'
import type { ElementType } from 'react'
import type { NavSection } from '@/types'
import { Dialog, Transition, Disclosure } from '@headlessui/react'
import { Link, useLocation } from 'react-router-dom'
import {
  XMarkIcon,
  ChevronDownIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
} from '@heroicons/react/24/outline'
import { Logo } from '../ui/Logo'
import { useAuth } from '@/contexts/AuthContext'
import { useTranslation } from 'react-i18next'
import { useI18n } from '@/contexts/I18nContext'
import { useQuery } from '@tanstack/react-query'
import { messagingApi } from '@/services/api/messaging.api'
import { getNavigationSections as buildNavigationSections } from './navigationSections'
import { usePendingAffiliations } from '@/hooks/usePendingAffiliations'
import { useAdminNavCounts } from './useAdminNavCounts'
import { useSidebarGroups } from './useSidebarGroups'
import { useDedicatedOnlyAffiliate } from '@/hooks/useDedicatedOnlyAffiliate'
import { usePayoutsEnabled } from '@/hooks/usePayoutsEnabled'
import clsx from 'clsx'

// #2273 — the closed state of a stored-state section renders no panel.
const NoPanel = () => null

interface SidebarProps {
  open: boolean
  onClose: () => void
  collapsed?: boolean
  onToggleCollapse?: () => void
}

export function Sidebar({ open, onClose, collapsed = false, onToggleCollapse }: SidebarProps) {
  const { user } = useAuth()
  const { t } = useTranslation(['common', 'dashboard', 'hotels', 'affiliates', 'reports'])
  const { isRTL } = useI18n()
  const location = useLocation()

  // Fetch unread messaging count
  const { data: unreadData } = useQuery({
    queryKey: ['messaging', 'unread-count'],
    queryFn: () => messagingApi.getUnreadCount(),
    refetchInterval: 30000,
    enabled: !!user,
  })
  const unreadCount = (unreadData as any)?.data?.unreadCount || 0

  // #1331 Part 3 + Data issues inbox — the admin-only rail counts (unread
  // WhatsApp threads, open data issues) live in useAdminNavCounts.
  const { whatsappUnreadCount, dataIssuesOpenCount } = useAdminNavCounts(user)

  // Pending affiliation-requests count for the rail badge — aggregated
  // over every ACTIVE hotel seat (was hotels[0] only, which hid requests
  // on seats 2..n and 403'd the badge when the first grant was revoked).
  const { count: pendingAffiliationsCount } = usePendingAffiliations()

  // #1210 — Discovery is hidden for users whose only affiliate access is
  // Dedicated Affiliates. Default-hidden while the check resolves so the
  // item never flashes in and disappears for exactly the users being gated.
  const { hideDiscovery, isResolved } = useDedicatedOnlyAffiliate()

  // #1998 B3 — Earnings & Payouts is hidden while payouts are switched off
  // here; unresolved counts as off so the entry never flashes in.
  const { enabled: payoutsEnabled } = usePayoutsEnabled(!!user)

  // Navigation sections — single source of truth lives in
  // ./navigationSections.ts so the desktop sidebar and the mobile
  // bottom-nav drawer can never drift apart again. Add or rename a
  // route there, not here.
  const navigationSections = buildNavigationSections({
    user,
    t,
    unreadCount,
    pendingAffiliationsCount,
    hideDiscovery: hideDiscovery || !isResolved,
    whatsappUnreadCount,
    dataIssuesOpenCount,
    hidePayouts: !payoutsEnabled,
  })

  const isItemActive = (href: string) => {
    // Handle URLs with query parameters (e.g., /reports/hotels?dashboard=1)
    const [hrefPath, hrefQuery] = href.split('?')
    const currentPath = location.pathname
    const currentSearch = location.search

    // If href has query params, check both path and query
    if (hrefQuery) {
      const hrefParams = new URLSearchParams(hrefQuery)
      const currentParams = new URLSearchParams(currentSearch)

      // Check if paths match and dashboard param matches
      if (currentPath === hrefPath) {
        const hrefDashboard = hrefParams.get('dashboard')
        const currentDashboard = currentParams.get('dashboard')
        if (hrefDashboard && currentDashboard === hrefDashboard) {
          return true
        }
      }
      return false
    }

    // Original logic for items without query params
    return currentPath === href ||
           (href !== '/dashboard' && currentPath.startsWith(href))
  }

  // #1410 — group-level collapse for the admin rail. Consecutive sections
  // that share a `group` (Admin / Hotels / Affiliates) render under one
  // toggleable header; ungrouped sections (Dashboard, and every non-admin
  // rail) render flat exactly as before. The state lives HERE rather than in
  // SidebarContent — that inner component is re-created on every render, so
  // hooks inside it would reset — and is shared by the mobile drawer and the
  // desktop rail. Persisted per user in localStorage (see useSidebarGroups).
  const activeGroup = navigationSections.find(
    section => !!section.group && section.items.some(item => isItemActive(item.href)),
  )?.group
  const { isGroupOpen, toggleGroup } = useSidebarGroups(user?.id, activeGroup)

  type NavRun = { group?: string; sections: NavSection[] }
  const navRuns = navigationSections.reduce<NavRun[]>((runs, section) => {
    const last = runs[runs.length - 1]
    if (last && last.group === section.group) last.sections.push(section)
    else runs.push({ group: section.group, sections: [section] })
    return runs
  }, [])

  // Sidebar content for full width mode. `idPrefix` keeps the group panel ids
  // unique when the mobile drawer and the desktop rail are both mounted.
  const SidebarContent = ({ isCollapsed = false, idPrefix = 'sidebar' }: { isCollapsed?: boolean; idPrefix?: string }) => {
    const renderSection = (section: NavSection) => {
      const hasActiveItem = section.items.some(item => isItemActive(item.href))
      const isSingleItem = section.items.length === 1 && section.items[0]?.label === section.label

      // Collapsed mode - show only icons with first item link
      if (isCollapsed) {
        return (
          <li key={section.label}>
            <Link
              to={section.items[0]?.href || '#'}
              className={clsx(
                'group flex w-full items-center justify-center h-9 rounded-[10px] p-2 transition-all duration-150 ease-in-out',
                hasActiveItem
                  ? 'bg-primary-50 text-primary-600'
                  : 'text-neutral-700 hover:bg-neutral-100 hover:text-neutral-900'
              )}
              title={section.label}
              onClick={onClose}
            >
              {section.icon && (
                <section.icon
                  className={clsx(
                    hasActiveItem ? 'text-primary-600' : 'text-neutral-400 group-hover:text-neutral-600',
                    'h-5 w-5 shrink-0'
                  )}
                  aria-hidden="true"
                />
              )}
            </Link>
          </li>
        )
      }

      if (isSingleItem) {
        const item = section.items[0]
        const isActive = isItemActive(item.href)
        const hasComingSoonBadge = item.badge === t('dashboard:hotelMenu.comingSoon')

        return (
          <li key={section.label}>
            <Link
              to={hasComingSoonBadge ? '#' : item.href}
              className={clsx(
                'group flex w-full items-center justify-between h-9 rounded-[10px] px-3 py-2 text-sm font-normal leading-5 transition-all duration-150 ease-in-out',
                isActive
                  ? 'bg-primary-50 text-primary-600'
                  : hasComingSoonBadge
                    ? 'text-neutral-400 cursor-default'
                    : 'text-neutral-700 hover:bg-neutral-100 hover:text-neutral-900'
              )}
              onClick={hasComingSoonBadge ? (e) => e.preventDefault() : onClose}
            >
              <div className="flex items-center gap-3">
                {section.icon && (
                  <section.icon
                    className={clsx(
                      isActive ? 'text-primary-600' : hasComingSoonBadge ? 'text-neutral-300' : 'text-neutral-400 group-hover:text-neutral-600',
                      'h-5 w-5 shrink-0'
                    )}
                    aria-hidden="true"
                  />
                )}
                {section.label}
              </div>
              {item.badge && (
                <span className="rounded-full bg-secondary-100 px-2.5 py-0.5 text-xs font-medium text-secondary-500">
                  {item.badge}
                </span>
              )}
            </Link>
          </li>
        )
      }

      // Header button + item list, shared by the Disclosure-driven sections and
      // the #2273 stored-state sections below.
      const renderBody = (
        open: boolean,
        ToggleButton: ElementType,
        Panel: ElementType,
        toggleProps: Record<string, unknown> = {},
      ) => (
        <>
          <ToggleButton
            {...toggleProps}
            className={clsx(
              'group flex w-full items-center justify-between h-9 rounded-[10px] px-3 py-2 text-sm font-normal leading-5 transition-all duration-150 ease-in-out',
              hasActiveItem
                ? 'bg-primary-50 text-primary-600'
                : 'text-neutral-700 hover:bg-neutral-100 hover:text-neutral-900'
            )}
          >
            <div className="flex items-center gap-3">
              {section.icon && (
                <section.icon
                  className={clsx(
                    hasActiveItem ? 'text-primary-600' : 'text-neutral-400 group-hover:text-neutral-600',
                    'h-5 w-5 shrink-0'
                  )}
                  aria-hidden="true"
                />
              )}
              {section.label}
            </div>
            <div className="flex items-center gap-2">
              {/* #1331 Part 3 — a section-level count, visible while the section is collapsed. */}
              {!open && section.badge && (
                <span className="rounded-full bg-secondary-100 px-2 py-0.5 text-xs font-medium text-secondary-500" data-testid="nav-section-badge">
                  {section.badge}
                </span>
              )}
              <ChevronDownIcon
                className={clsx(
                  open ? 'rotate-180' : '',
                  'h-4 w-4 text-neutral-400 transition-transform duration-150'
                )}
              />
            </div>
          </ToggleButton>

          <Panel className="mt-1">
            <ul className="space-y-1 ps-8">
              {section.items.map((item) => {
                const isActive = isItemActive(item.href)
                return (
                  <li key={item.label}>
                    <Link
                      to={item.href}
                      onClick={onClose}
                      className={clsx(
                        isActive
                          ? 'bg-primary-50 text-primary-600'
                          : 'text-neutral-600 hover:text-neutral-900 hover:bg-neutral-100',
                        'group flex items-center justify-between h-9 rounded-[10px] px-3 py-2 text-sm font-normal leading-5 transition-all duration-150 ease-in-out'
                      )}
                    >
                      <div className="flex items-center gap-3">
                        {item.icon && (
                          <item.icon
                            className={clsx(
                              isActive ? 'text-primary-600' : 'text-neutral-400 group-hover:text-neutral-600',
                              'h-4 w-4 shrink-0'
                            )}
                            aria-hidden="true"
                          />
                        )}
                        {item.label}
                      </div>
                      {item.badge && (
                        <span className="rounded-full bg-secondary-100 px-2.5 py-0.5 text-xs font-medium text-secondary-500">
                          {item.badge}
                        </span>
                      )}
                    </Link>
                  </li>
                )
              })}
            </ul>
          </Panel>
        </>
      )

      // #2273 — a section with an `expandKey` (hotel / affiliate Reports)
      // starts expanded and follows the per-user stored state (shared with the
      // #1410 group headers), so a manual collapse survives re-renders and
      // navigation instead of being reset by the Disclosure remount below.
      if (section.expandKey) {
        const expandKey = section.expandKey
        const open = isGroupOpen(expandKey)
        return (
          <li key={section.label}>
            {renderBody(
              open,
              'button',
              open ? 'div' : NoPanel,
              { type: 'button', 'aria-expanded': open, onClick: () => toggleGroup(expandKey) },
            )}
          </li>
        )
      }

      // Full width mode - the per-section Disclosure opens when it holds the
      // active route (the key remounts it on route change so defaultOpen
      // re-applies).
      return (
        <li key={`${section.label}-${hasActiveItem}`}>
          <Disclosure defaultOpen={hasActiveItem}>
            {({ open }) => renderBody(open, Disclosure.Button, Disclosure.Panel)}
          </Disclosure>
        </li>
      )
    }

    return (
    <div id="navigation" className={clsx(
      "flex grow flex-col gap-y-5 overflow-y-auto bg-white py-6",
      isCollapsed ? "px-2" : "px-4"
    )}>
      <div className={clsx(
        "flex shrink-0 items-center justify-center h-14 py-0",
        isCollapsed ? "flex-col" : "flex-col"
      )}>
        {isCollapsed ? (
          <img src="/favicon-32x32.png" alt="Hoteliate" className="w-8 h-8 mx-auto" />
        ) : (
          // #683 §1.3: pin the sidebar logo to h-9 (36px, −10% from the
          // shared lg=h-10) via imgClassName so the override is scoped
          // here and the auth-page lg logos stay at 40px. Slogan moved
          // to the Header (§1.4).
          <Logo size="lg" imgClassName="!h-9" showSlogan={false} />
        )}
      </div>

      {/* The rail itself scrolls (overflow-y-auto above) so expanded groups
          never push the sidebar past the viewport — the "graceful container
          scroll" option of #1410 rather than nested per-group scrollbars. */}
      <nav className="flex flex-1 flex-col">
        <ul role="list" className="flex flex-1 flex-col gap-y-1">
          {navRuns.map((run, runIdx) => {
            // Ungrouped sections: flat, exactly as before #1410.
            if (!run.group) return run.sections.map(renderSection)

            const group = run.group
            const groupLabel = t(`dashboard:navGroup.${group}`, { defaultValue: group })

            // Icon-only rail: no room for a header, so every group stays
            // visible and a thin divider marks the transition. #57: no
            // divider directly under the logo.
            if (isCollapsed) {
              return (
                <Fragment key={`group-${group}`}>
                  {runIdx > 0 && (
                    <li aria-hidden="true" className="my-2 mx-2 border-t border-neutral-200" />
                  )}
                  {run.sections.map(renderSection)}
                </Fragment>
              )
            }

            const open = isGroupOpen(group)
            const panelId = `${idPrefix}-group-${group}`
            // When the active route is inside a group the user collapsed,
            // tint the header so the page is still locatable from the rail.
            const hidesActive = !open && group === activeGroup
            return (
              <Fragment key={`group-${group}`}>
                <li className={clsx('px-1 pb-1', runIdx > 0 ? 'pt-3' : 'pt-1')}>
                  <div className={clsx(runIdx > 0 && 'border-t border-neutral-100 pt-3')}>
                    <button
                      type="button"
                      aria-expanded={open}
                      aria-controls={panelId}
                      onClick={() => toggleGroup(group)}
                      className={clsx(
                        'group flex w-full items-center justify-between rounded-[10px] px-2 py-1.5 text-[10px] font-semibold uppercase tracking-wider transition-colors duration-150 hover:bg-neutral-100 focus-visible:outline focus-visible:outline-2 focus-visible:outline-primary-500',
                        hidesActive ? 'text-primary-600' : 'text-neutral-400 hover:text-neutral-700'
                      )}
                    >
                      <span>{groupLabel}</span>
                      <ChevronDownIcon
                        className={clsx(
                          open ? 'rotate-180' : '',
                          'h-3.5 w-3.5 shrink-0 transition-transform duration-150'
                        )}
                        aria-hidden="true"
                      />
                    </button>
                  </div>
                </li>
                {open && (
                  <li id={panelId}>
                    <ul role="list" className="flex flex-col gap-y-1">
                      {run.sections.map(renderSection)}
                    </ul>
                  </li>
                )}
              </Fragment>
            )
          })}
        </ul>
      </nav>

      {/* Collapse/Expand toggle button - only show on desktop full content */}
      {!isCollapsed && onToggleCollapse && (
        <div className="mt-auto pt-2">
          <button
            type="button"
            onClick={onToggleCollapse}
            className="group flex w-full items-center justify-center gap-2 rounded-md px-3 py-2 text-sm font-medium text-neutral-600 hover:bg-neutral-50 hover:text-neutral-900 transition-colors duration-200"
          >
            {/* #2270: mirrored by the document direction (CSS), not the
                context flag, so the arrow always points at the sidebar's own
                edge: left in LTR, right in RTL. Same on the mobile drawer. */}
            <ChevronLeftIcon data-testid="sidebar-collapse-icon" className="h-5 w-5 text-neutral-400 group-hover:text-neutral-600 rtl:-scale-x-100" />
            <span>{t('common:collapseSidebar')}</span>
          </button>
        </div>
      )}
    </div>
    )
  }

  return (
    <>
      <Transition.Root show={open} as={Fragment}>
        <Dialog as="div" className="relative z-50 lg:hidden" onClose={onClose}>
          <Transition.Child
            as={Fragment}
            enter="transition-opacity ease-linear duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="transition-opacity ease-linear duration-300"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <div className="fixed inset-0 bg-neutral-900/80" />
          </Transition.Child>

          <div className={clsx("fixed inset-0 flex", isRTL && "flex-row-reverse")}>
            <Transition.Child
              as={Fragment}
              enter="transition ease-in-out duration-300 transform"
              enterFrom={isRTL ? "translate-x-full" : "-translate-x-full"}
              enterTo="translate-x-0"
              leave="transition ease-in-out duration-300 transform"
              leaveFrom="translate-x-0"
              leaveTo={isRTL ? "translate-x-full" : "-translate-x-full"}
            >
              <Dialog.Panel className="relative flex w-full max-w-xs flex-1 me-16">
                <Transition.Child
                  as={Fragment}
                  enter="ease-in-out duration-300"
                  enterFrom="opacity-0"
                  enterTo="opacity-100"
                  leave="ease-in-out duration-300"
                  leaveFrom="opacity-100"
                  leaveTo="opacity-0"
                >
                  <div className="absolute top-0 flex w-16 justify-center pt-5 end-full">
                    <button type="button" className="-m-2.5 p-2.5" onClick={onClose}>
                      <span className="sr-only">{t('common:closeSidebar')}</span>
                      <XMarkIcon className="h-6 w-6 text-white" aria-hidden="true" />
                    </button>
                  </div>
                </Transition.Child>
                <SidebarContent idPrefix="drawer" />
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </Dialog>
      </Transition.Root>

      {/* Static sidebar for desktop */}
      <div className={clsx(
        "hidden lg:fixed lg:inset-y-0 lg:z-40 lg:flex lg:flex-col transition-all duration-300 lg:start-0",
        collapsed ? "lg:w-20" : "lg:w-72"
      )}>
        <div className="flex grow flex-col gap-y-5 overflow-y-auto bg-white border-e border-neutral-200">
          <SidebarContent isCollapsed={collapsed} idPrefix="desktop" />

          {/* Expand button when collapsed */}
          {collapsed && onToggleCollapse && (
            <div className="mt-auto pb-4 px-2">
              <button
                type="button"
                onClick={onToggleCollapse}
                className="group flex w-full items-center justify-center rounded-md p-3 text-neutral-600 hover:bg-neutral-50 hover:text-neutral-900 transition-colors duration-200"
                title={t('common:expandSidebar')}
              >
                <ChevronRightIcon data-testid="sidebar-expand-icon" className="h-5 w-5 text-neutral-400 group-hover:text-neutral-600 rtl:-scale-x-100" />
              </button>
            </div>
          )}
        </div>
      </div>
    </>
  )
}
